import { useMemo, useRef, useState } from 'react';
import { Button, Input, message, Select, Typography } from 'antd';
import type { SelectProps } from 'antd/es/select';
import {
	POLICY_CONFIGURATION_BUILDING_BLOCKS,
	POLICY_CONFIGURATION_BUILDING_BLOCK_MAP,
	getPolicyConfigurationSubcategories,
} from '../constants/buildingBlocks';
import { createPolicyAnalysis } from '../../../infrastructure/apiClient';
import { useClients } from '../../../context/ClientContext';
import styles from './PolicyAnalysis.module.scss';

const { Paragraph } = Typography;

export type PolicyAnalysisPayload = {
	configurationType: number;
	configurationSubtype: string;
	comparisonName: string;
	continent: string;
	country: string;
	state: string;
};

interface PolicyAnalysisProps {
	onCompare?: (payload: PolicyAnalysisPayload) => Promise<void> | void;
}

const PolicyAnalysis = ({ onCompare }: PolicyAnalysisProps) => {
	const [buildingBlockKey, setBuildingBlockKey] = useState('');
	const [subcategoryKey, setSubcategoryKey] = useState('');
	const [analysisName, setAnalysisName] = useState('');
	const [continent, setContinent] = useState('');
	const [country, setCountry] = useState('');
	const [stateValue, setStateValue] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const inFlight = useRef(false);
	const { activeClient } = useClients();

	const buildingBlockOptions = useMemo<SelectProps['options']>(
		() =>
			POLICY_CONFIGURATION_BUILDING_BLOCKS.map((block) => ({
				value: block.key,
				label: block.label,
			})),
		[],
	);

	const subcategoryOptions = useMemo<SelectProps['options']>(
		() =>
			getPolicyConfigurationSubcategories(buildingBlockKey).map((subcategory) => ({
				value: subcategory.label,
				label: subcategory.label,
			})),
		[buildingBlockKey],
	);

	const handleBuildingBlockChange = (value?: string) => {
		setBuildingBlockKey(value ?? '');
		setSubcategoryKey('');
	};

	const handleCompare = async () => {
		if (inFlight.current) return;
		if (!buildingBlockKey) {
			message.warning('Select a building block.');
			return;
		}

		if (!subcategoryKey.trim()) {
			message.warning('Select a subcategory.');
			return;
		}

		if (!analysisName.trim()) {
			message.warning('Enter a analysis name.');
			return;
		}

		if (!continent.trim()) {
			message.warning('Enter a continent.');
			return;
		}

		if (!country.trim()) {
			message.warning('Enter a country.');
			return;
		}

		if (!stateValue.trim()) {
			message.warning('Enter a state.');
			return;
		}

		if (!activeClient?.id) {
			message.warning('Select a tenant before comparing policies.');
			return;
		}

		const selectedBlock = POLICY_CONFIGURATION_BUILDING_BLOCK_MAP[buildingBlockKey];
		const blockCode = Number(selectedBlock?.code);
		if (!selectedBlock || Number.isNaN(blockCode)) {
			message.error('Unable to resolve the selected building block.');
			return;
		}

		const trimmedSubcategoryLabel = subcategoryKey.trim();

		try {
			inFlight.current = true;
			setSubmitting(true);
			const payload = {
				configurationType: blockCode,
				configurationSubtype: trimmedSubcategoryLabel,
				comparisonName: analysisName.trim(),
				continent: continent.trim(),
				country: country.trim(),
				state: stateValue.trim(),
			};
			await createPolicyAnalysis(activeClient.id, payload);
			message.success('Policy analysis started.');
			if (onCompare) {
				await onCompare(payload);
			}
		} catch (error) {
			message.error('Unable to start policy analysis.');
		} finally {
			inFlight.current = false;
			setSubmitting(false);
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.selectGroup}>
				<Paragraph className="!text-[#9ca3af] !mb-0">
					Select a building block, sub-type and region to analyze.
				</Paragraph>
				<div className={styles.selectRow}>
					<Input
						placeholder="Analysis Name"
						value={analysisName}
						onChange={(event) => setAnalysisName(event.target.value)}
						disabled={submitting}
						required
						className={styles.select}
					/>
				</div>
				<div className={styles.selectRow}>
					<Select
						placeholder="Select building block"
						options={buildingBlockOptions}
						value={buildingBlockKey || undefined}
						onChange={handleBuildingBlockChange}
						allowClear
						disabled={submitting}
						className={styles.select}
					/>
				</div>
				<div className={styles.selectRow}>
					<Select
						placeholder="Select sub-type"
						options={subcategoryOptions}
						value={subcategoryKey || undefined}
						onChange={(value) => setSubcategoryKey(typeof value === 'string' ? value : '')}
						allowClear
						disabled={submitting || !buildingBlockKey}
						className={styles.select}
					/>
				</div>
				<div className={styles.selectRow}>
					<Input
						placeholder="Continent"
						value={continent}
						onChange={(event) => setContinent(event.target.value)}
						disabled={submitting}
						required
						className={styles.select}
					/>
				</div>
				<div className={styles.selectRow}>
					<Input
						placeholder="Country"
						value={country}
						onChange={(event) => setCountry(event.target.value)}
						disabled={submitting}
						required
						className={styles.select}
					/>
				</div>
				<div className={styles.selectRow}>
					<Input
						placeholder="State"
						value={stateValue}
						onChange={(event) => setStateValue(event.target.value)}
						disabled={submitting}
						required
						className={styles.select}
					/>
				</div>
			</div>
			<div className={styles.actions}>
				<Button
					type="primary"
					onClick={handleCompare}
					loading={submitting}
					disabled={submitting}
					className={styles.compareButton}
				>
					Compare
				</Button>
			</div>
		</div>
	);
};

export default PolicyAnalysis;
